import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import type * as MonacoType from 'monaco-editor';
import {
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
  FormControl,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
// Switch removed — not currently used in this component
import { Label } from '@/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { ChevronDown, ChevronUp, LayoutTemplate, Code2, Eye, Pencil, Paperclip, Plus, Trash2, Info } from 'lucide-react';
import { BaseFieldProps } from './types';
import { EmailTemplateService } from '@/features/Settings/services/emailTemplateService';
import type { AvailableTemplate } from '@/features/Settings/types/EmailTemplateTypes';
import { useTheme } from '@/shared/hooks/use-theme';

const INTERNAL_KEYS = new Set([
  'nodeId', 'executionId', 'organizationId', 'workspaceId',
  'agentBaseId', 'parameters', 'prompts', '_webhook',
]);

function resolveUserLanguage(i18nLang: string): string {
  const primary = i18nLang.split('-')[0].toLowerCase();
  const map: Record<string, string> = {
    en: 'en-US', pt: 'pt-BR', es: 'es-ES', fr: 'fr-FR', de: 'de-DE',
  };
  return map[primary] ?? i18nLang;
}

type Mode = 'template' | 'html';

/** Per-variable mapping entry: select a workflow field OR type a free value. */
type MappingEntry =
  | { mode: 'select'; inputKey: string }
  | { mode: 'free'; value: string };

interface EmailServicePlusFieldProps extends BaseFieldProps {}

export const EmailServicePlusField: React.FC<EmailServicePlusFieldProps> = ({
  field,
  name,
  control,
  formValues,
  nodeInputData,
}) => {
  const { setValue } = useFormContext();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const userLanguage = resolveUserLanguage(i18n.language);
  const editorRef = useRef<MonacoType.editor.IStandaloneCodeEditor | null>(null);

  // ── Mode ──────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<Mode>(
    formValues?.use_template === false ? 'html' : 'template',
  );

  const switchMode = (next: Mode) => {
    setMode(next);
    setValue('use_template', next === 'template');
  };

  // ── Template mode state ───────────────────────────────────────────────────
  const [showDetails, setShowDetails] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  /**
   * Per-variable mapping.
   * mode === 'select' → resolves to ${input.inputKey}
   * mode === 'free'   → uses the raw value (supports {{input.*}} placeholders)
   */
  const [mapping, setMapping] = useState<Record<string, MappingEntry>>({});

  const savedTemplateLang = (formValues?.template_language as string) || '';
  const isInitiallyDynamicLang = savedTemplateLang.includes('${') || savedTemplateLang.includes('{{');

  const [activeLanguage, setActiveLanguage] = useState<string>(
    isInitiallyDynamicLang ? userLanguage : (savedTemplateLang || userLanguage),
  );

  const [languageDynamic, setLanguageDynamic] = useState<boolean>(isInitiallyDynamicLang);
  const [dynamicLanguageValue, setDynamicLanguageValue] = useState<string>(
    isInitiallyDynamicLang ? savedTemplateLang : '',
  );

  // Subject override for template mode (optional — overrides the template subject)
  const [subjectOverride, setSubjectOverride] = useState<string>(
    (formValues?.subject_override as string) ?? '',
  );

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates', 'available'],
    queryFn: () => EmailTemplateService.getAvailableTemplates(undefined),
  });

  const inputKeys = Object.keys(nodeInputData ?? {}).filter((k) => !INTERNAL_KEYS.has(k));

  const dropdownTemplates = useMemo(() => {
    const bySlug = new Map<string, AvailableTemplate>();
    for (const t of templates) {
      const existing = bySlug.get(t.slug);
      if (!existing || (t.language === userLanguage && existing.language !== userLanguage)) {
        bySlug.set(t.slug, t);
      }
    }
    return Array.from(bySlug.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, userLanguage]);

  const selectedSlug = (formValues?.template_slug as string) ?? '';

  const availableLanguages = useMemo(
    () => templates.filter((t) => t.slug === selectedSlug).map((t) => t.language),
    [templates, selectedSlug],
  );

  const getSelectedTemplate = (): AvailableTemplate | undefined => {
    if (!selectedSlug) return undefined;
    return (
      templates.find((t) => t.slug === selectedSlug && t.language === activeLanguage) ??
      templates.find((t) => t.slug === selectedSlug)
    );
  };

  /** Convert the internal mapping state to the form value saved in config. */
  const buildVars = (m: Record<string, MappingEntry>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [templateKey, entry] of Object.entries(m)) {
      if (entry.mode === 'select' && entry.inputKey && entry.inputKey !== '__CLEAR__') {
        result[templateKey] = `\${input.${entry.inputKey}}`;
      } else if (entry.mode === 'free' && entry.value !== '') {
        result[templateKey] = entry.value;
      }
    }
    return result;
  };

  const updateMapping = (next: Record<string, MappingEntry>) => {
    setMapping(next);
    setValue('template_variables', buildVars(next));
  };

  const handleSelectChange = (templateKey: string, inputKey: string) => {
    const next = { ...mapping };
    if (inputKey === '__CLEAR__') {
      next[templateKey] = { mode: 'select', inputKey: '' };
    } else {
      next[templateKey] = { mode: 'select', inputKey };
    }
    updateMapping(next);
  };

  const handleFreeValueChange = (templateKey: string, value: string) => {
    const next = { ...mapping, [templateKey]: { mode: 'free' as const, value } };
    updateMapping(next);
  };

  const toggleEntryMode = (templateKey: string) => {
    const current = mapping[templateKey];
    const next = { ...mapping };
    if (!current || current.mode === 'select') {
      next[templateKey] = { mode: 'free', value: '' };
    } else {
      next[templateKey] = { mode: 'select', inputKey: '' };
    }
    updateMapping(next);
  };

  const applyAutoMapping = (template: AvailableTemplate) => {
    const auto: Record<string, MappingEntry> = {};
    for (const v of template.variables) {
      if (inputKeys.includes(v.key)) {
        auto[v.key] = { mode: 'select', inputKey: v.key };
      }
    }
    updateMapping(auto);
  };

  const handleLanguageSwitch = (lang: string) => {
    setActiveLanguage(lang);
    setValue('template_language', lang);
    const template = templates.find((t) => t.slug === selectedSlug && t.language === lang);
    if (template) applyAutoMapping(template);
  };

  const toggleLanguageDynamic = () => {
    if (languageDynamic) {
      setLanguageDynamic(false);
      setValue('template_language', activeLanguage);
    } else {
      setLanguageDynamic(true);
      setValue('template_language', dynamicLanguageValue);
    }
  };

  const handleDynamicLanguageChange = (val: string) => {
    setDynamicLanguageValue(val);
    setValue('template_language', val);
  };

  const handleSubjectOverrideChange = (val: string) => {
    setSubjectOverride(val);
    setValue('subject_override', val);
  };

  // Restore mapping from saved form values on mount
  useEffect(() => {
    const saved = formValues?.template_variables;
    if (saved && typeof saved === 'object') {
      const restored: Record<string, MappingEntry> = {};
      for (const [k, v] of Object.entries(saved as Record<string, string>)) {
        if (typeof v !== 'string') continue;
        const match = v.match(/^\$\{input\.(.+)\}$/);
        if (match) {
          restored[k] = { mode: 'select', inputKey: match[1] };
        } else {
          restored[k] = { mode: 'free', value: v };
        }
      }
      if (Object.keys(restored).length > 0) setMapping(restored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Attachments state (shared across both modes) ──────────────────────────
  type AttachmentEntry = { enabled?: string; filename: string; content: string; format: string };

  const [attachments, setAttachments] = useState<AttachmentEntry[]>(() => {
    const saved = formValues?.attachments;
    if (Array.isArray(saved) && saved.length > 0) return saved as AttachmentEntry[];
    return [];
  });
  const [attachmentsOpen, setAttachmentsOpen] = useState(attachments.length > 0);

  const updateAttachments = (next: AttachmentEntry[]) => {
    setAttachments(next);
    setValue('attachments', next);
  };

  const addAttachment = () => {
    updateAttachments([...attachments, { filename: '', content: '', format: 'pdf' }]);
    setAttachmentsOpen(true);
  };

  const removeAttachment = (index: number) => {
    updateAttachments(attachments.filter((_, i) => i !== index));
  };

  const updateAttachmentField = (index: number, key: keyof AttachmentEntry, value: string) => {
    const next = [...attachments];
    next[index] = { ...next[index], [key]: value };
    updateAttachments(next);
  };

  // ── HTML mode state ───────────────────────────────────────────────────────
  const [subject, setSubject] = useState<string>((formValues?.subject as string) ?? '');
  const [body, setBody] = useState<string>((formValues?.body as string) ?? '');
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);

  const handleSubjectChange = (val: string) => {
    setSubject(val);
    setValue('subject', val);
  };

  const handleBodyChange = (val: string | undefined) => {
    const v = val ?? '';
    setBody(v);
    setValue('body', v);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        const selected = getSelectedTemplate();
        const hasVariables = (selected?.variables?.length ?? 0) > 0;
        const hasInputKeys = inputKeys.length > 0;

        return (
          <FormItem>
            <FormLabel>{field.label ?? t('workflow.email.contentLabel')}</FormLabel>

            {/* Mode toggle */}
            <div className="flex items-center gap-1 rounded-md border bg-background p-0.5 w-fit mb-2">
              <button
                type="button"
                onClick={() => switchMode('template')}
                className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs transition-colors ${
                  mode === 'template'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutTemplate className="h-3.5 w-3.5" />
                Template
              </button>
              <button
                type="button"
                onClick={() => switchMode('html')}
                className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs transition-colors ${
                  mode === 'html'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Code2 className="h-3.5 w-3.5" />
                {t('workflow.email.customHtml')}
              </button>
            </div>

            <FormControl>
              {mode === 'template' ? (
                /* ── TEMPLATE MODE ── */
                <div className="space-y-3">
                  {/* Template dropdown */}
                  <Select
                    onValueChange={(value) => {
                      if (value === '__CLEAR__') {
                        formField.onChange('');
                        setValue('template_language', '');
                        updateMapping({});
                        setActiveLanguage(userLanguage);
                        setLanguageDynamic(false);
                        setDynamicLanguageValue('');
                      } else {
                        formField.onChange(value);
                        const preferred =
                          templates.find((t) => t.slug === value && t.language === userLanguage) ??
                          templates.find((t) => t.slug === value);
                        const lang = preferred?.language ?? userLanguage;
                        setActiveLanguage(lang);
                        if (!languageDynamic) {
                          setValue('template_language', lang);
                        }
                        if (preferred) applyAutoMapping(preferred);
                      }
                      setShowDetails(false);
                    }}
                    value={selectedSlug || undefined}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoading ? t('workflow.email.loadingTemplates') : t('workflow.email.selectTemplate')} />
                    </SelectTrigger>
                    <SelectContent>
                      {formField.value && (
                        <SelectItem value="__CLEAR__">{t('workflow.email.noTemplate')}</SelectItem>
                      )}
                      {dropdownTemplates.map((tmpl) => (
                        <SelectItem key={tmpl.slug} value={tmpl.slug}>
                          <span className="flex items-center gap-2">
                            {tmpl.name}
                            {tmpl.source === 'org' && (
                              <Badge variant="secondary" className="text-xs py-0 px-1">
                                {t('workflow.email.customBadge')}
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Language switcher */}
                  {selectedSlug && availableLanguages.length > 0 && (
                    <div>
                      {languageDynamic ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-muted-foreground">
                              {t('workflow.email.dynamicLanguageLabel')}
                            </Label>
                            <button
                              type="button"
                              title={t('workflow.email.useFixedLanguage')}
                              onClick={toggleLanguageDynamic}
                              className="rounded p-1 text-primary bg-primary/10 transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <Input
                            className="h-7 text-xs font-mono"
                            value={dynamicLanguageValue}
                            onChange={(e) => handleDynamicLanguageChange(e.target.value)}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {availableLanguages.length === 1 ? (
                            <p className="text-xs text-muted-foreground">
                              {t('workflow.email.noTranslation')}
                            </p>
                          ) : (
                            <div className="flex gap-1 rounded-md border bg-background p-0.5 w-fit">
                              {availableLanguages.map((lang) => (
                                <button
                                  key={lang}
                                  type="button"
                                  onClick={() => handleLanguageSwitch(lang)}
                                  className={`rounded px-2 py-0.5 text-xs transition-colors ${
                                    activeLanguage === lang
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}
                                >
                                  {lang}
                                </button>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            title={t('workflow.email.useDynamicLanguage')}
                            onClick={toggleLanguageDynamic}
                            className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Subject override */}
                  {selectedSlug && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-muted-foreground">
                        {t('workflow.email.subjectOverrideLabel')}
                      </Label>
                      <Input
                        value={subjectOverride}
                        onChange={(e) => handleSubjectOverrideChange(e.target.value)}
                        placeholder={selected?.subject ?? t('workflow.email.keepOriginalSubject')}
                        className="text-sm"
                      />
                    </div>
                  )}

                  {/* Variable mapper */}
                  {selected && hasVariables && (
                    <div className="rounded-md border bg-muted/40 p-3 text-sm">
                      <p className="font-medium mb-2 text-xs">{t('workflow.email.mapVariables')}</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1 pr-2 font-medium text-muted-foreground w-1/3">
                              {t('workflow.email.variable')}
                            </th>
                            <th className="text-left py-1 font-medium text-muted-foreground">
                              {t('workflow.email.value')}
                            </th>
                            <th className="w-8" />
                          </tr>
                        </thead>
                        <tbody>
                          {selected.variables.map((v) => {
                            const entry = mapping[v.key];
                            const isFree = entry?.mode === 'free';

                            return (
                              <tr key={v.key} className="border-b last:border-0">
                                <td className="py-1.5 pr-3 font-mono text-xs align-top pt-2">
                                  {`{{${v.key}}}`}
                                </td>
                                <td className="py-1.5 pr-1">
                                  {isFree ? (
                                    <Input
                                      className="h-7 text-xs"
                                      value={(entry as { mode: 'free'; value: string }).value}
                                      onChange={(e) =>
                                        handleFreeValueChange(v.key, e.target.value)
                                      }
                                      placeholder={t('workflow.email.freeValuePlaceholder')}
                                    />
                                  ) : (
                                    <Select
                                      value={
                                        entry?.mode === 'select' ? (entry.inputKey || '') : ''
                                      }
                                      onValueChange={(val) => handleSelectChange(v.key, val)}
                                      disabled={!hasInputKeys}
                                    >
                                      <SelectTrigger className="h-7 text-xs">
                                        <SelectValue
                                          placeholder={
                                            hasInputKeys ? t('workflow.email.notMapped') : t('workflow.email.noInputFields')
                                          }
                                        />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__CLEAR__">{t('workflow.email.notMapped')}</SelectItem>
                                        {inputKeys.map((k) => (
                                          <SelectItem key={k} value={k}>
                                            {k}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </td>
                                {/* Toggle: workflow field ↔ free input */}
                                <td className="py-1.5 pl-1 align-top pt-2">
                                  <button
                                    type="button"
                                    title={
                                      isFree
                                        ? t('workflow.email.useWorkflowField')
                                        : t('workflow.email.typeManually')
                                    }
                                    onClick={() => toggleEntryMode(v.key)}
                                    className={`rounded p-1 transition-colors ${
                                      isFree
                                        ? 'text-primary bg-primary/10'
                                        : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Template details collapsible */}
                  {selected && (
                    <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-2">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between font-medium text-left"
                        onClick={() => setShowDetails((v) => !v)}
                      >
                        <span>{t('workflow.email.templateDetails')}</span>
                        {showDetails ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>

                      {showDetails && (
                        <div className="space-y-3 pt-1">
                          <div>
                            <span className="text-muted-foreground">{t('workflow.email.originalSubject')}</span>
                            <span>{selected.subject}</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowPreview(true)}
                          >
                            {t('workflow.email.preview')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Template preview dialog */}
                  {showPreview && selected && (
                    <Dialog
                      open={showPreview}
                      onOpenChange={(o) => !o && setShowPreview(false)}
                    >
                      <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
                        <DialogHeader>
                          <DialogTitle>{t('workflow.email.previewTitle', { name: selected.name })}</DialogTitle>
                        </DialogHeader>
                        <div className="flex-1 min-h-0 overflow-hidden rounded border">
                          <iframe
                            sandbox="allow-same-origin allow-popups"
                            srcDoc={selected.content}
                            title="Template Preview"
                            className="w-full h-full border-0"
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              ) : (
                /* ── HTML MODE ── */
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">{t('workflow.email.subject')}</Label>
                    <Input
                      value={subject}
                      onChange={(e) => handleSubjectChange(e.target.value)}
                      placeholder={t('workflow.email.subjectPlaceholder')}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{t('workflow.email.bodyHtml')}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowHtmlPreview((v) => !v)}
                        className="h-7 gap-1 text-xs"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {showHtmlPreview ? t('workflow.email.hidePreview') : t('workflow.email.preview')}
                      </Button>
                    </div>

                    <div className={showHtmlPreview ? 'grid grid-cols-2 gap-2' : undefined}>
                      <div className="rounded border overflow-hidden">
                        <Editor
                          height="320px"
                          language="html"
                          value={body}
                          onChange={handleBodyChange}
                          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                          options={{
                            minimap: { enabled: false },
                            wordWrap: 'on',
                            fontSize: 13,
                            lineHeight: 20,
                          }}
                          onMount={(editor) => {
                            editorRef.current = editor;
                          }}
                        />
                      </div>

                      {showHtmlPreview && (
                        <div className="rounded border overflow-hidden" style={{ height: 320 }}>
                          <iframe
                            sandbox="allow-same-origin allow-popups"
                            srcDoc={
                              body ||
                              `<p style="color:#888;padding:16px">${t('workflow.email.nothingToPreview')}</p>`
                            }
                            title="HTML Preview"
                            className="w-full h-full border-0"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </FormControl>

            {/* ── Attachments section (shared across both modes) ── */}
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs px-2"
                  onClick={() => setAttachmentsOpen((v) => !v)}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  Anexos ({attachments.length})
                  {attachmentsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
                <span title={'Anexe arquivos ao email usando placeholders de Node Output Memory.\nEx: ${"Clean Transcription".output.content}'}>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </span>
              </div>

              {attachmentsOpen && (
                <div className="space-y-3 mt-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="rounded-md border bg-muted/40 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Anexo {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={() => removeAttachment(idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Habilitado (placeholder ou true/false)</Label>
                        <Input
                          className="h-7 text-xs"
                          value={att.enabled ?? ''}
                          onChange={(e) => updateAttachmentField(idx, 'enabled', e.target.value)}
                          placeholder='${parameters.send_transcript} ou true/false'
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Nome do arquivo (sem extensão)</Label>
                        <Input
                          className="h-7 text-xs"
                          value={att.filename}
                          onChange={(e) => updateAttachmentField(idx, 'filename', e.target.value)}
                          placeholder='Transcription_${"Summarize Meeting".output.meeting.title}'
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Conteúdo (placeholder)</Label>
                        <Input
                          className="h-7 text-xs"
                          value={att.content}
                          onChange={(e) => updateAttachmentField(idx, 'content', e.target.value)}
                          placeholder='${"Clean Transcription".output.content}'
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Formato (pdf, md, txt ou placeholder)</Label>
                        <Input
                          className="h-7 text-xs"
                          value={att.format}
                          onChange={(e) => updateAttachmentField(idx, 'format', e.target.value)}
                          placeholder='pdf, md, txt ou ${parameters.transcript_format}'
                        />
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={addAttachment}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Adicionar anexo
                  </Button>
                </div>
              )}
            </div>

            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default EmailServicePlusField;
